import { Tooltip, Typography } from '@mui/material';
import type { AccessCost } from './suggestionsApi';
import { tollsSentences, type RouteFigure } from './routeFigure';

const pln = (v: number | null) => (v === null ? 'nieznany' : `${v.toLocaleString('pl-PL')} zł`);

/**
 * Koszt dolotu przy kandydacie (I.02).
 *
 * Liczba jest szacunkiem z szacunku (dolot ±8%, czas ze średniej prędkości),
 * więc zawsze stoi z „~” i z zastrzeżeniami w podpowiedzi. Gdy brakuje
 * parametru od Marathonu, piszemy „nieznany” i wymieniamy braki — pusta
 * komórka czytałaby się jak „za darmo”.
 *
 * `compact` — druga linia pod dolotem w tabeli podpowiedzi: mniejsza czcionka
 * i słowo „koszt”, bo bez nagłówka kolumny samo „nieznany” nic nie mówi.
 * Kwota nigdy się nie łamie (`nowrap`) — „~492 / zł” w dwóch liniach czyta
 * się jak dwie liczby.
 *
 * `route` — dolot z podpisanym źródłem (06.10.2026): w dymku linia o opłatach
 * drogowych z HERE („opłaty drogowe ~85 zł (20 EUR, wg HERE)”), a przy
 * dolocie bez map — „bez opłat drogowych”. Brak (starsze API) — jak dotąd.
 */
export const AccessCostCell = ({
  cost,
  compact = false,
  route,
}: {
  cost?: AccessCost;
  compact?: boolean;
  route?: RouteFigure | null;
}) => {
  const variant = compact ? 'caption' : 'body2';
  const sx = { whiteSpace: 'nowrap', display: 'block' } as const;
  const prefix = compact ? 'koszt ' : '';
  if (!cost)
    return (
      <Typography variant={variant} color="text.secondary" sx={sx}>
        {compact ? 'koszt —' : '—'}
      </Typography>
    );

  const caveats = cost.assumptions.join('; ');
  if (cost.totalPln === null) {
    return (
      <Tooltip
        title={`Brakuje danych do policzenia kosztu: ${cost.missing.join(', ')}. Stawki ustawia sekcja administracyjna na ekranie spółek.`}
      >
        <Typography variant={variant} color="text.secondary" sx={sx}>
          {prefix}nieznany
        </Typography>
      </Tooltip>
    );
  }
  const c = cost.components;
  // Rachunek z kursem NBP, gdy API go podaje; starsze API — same kwoty.
  const detail = cost.breakdown?.length
    ? cost.breakdown.join('; ')
    : `Paliwo ${pln(c.fuelPln)}, kierowca ${pln(c.driverPln)}, ciągnik ${pln(c.tractorPln)}`;
  const tolls = tollsSentences(cost.breakdown, [{ figure: route }]);
  const title = [`${detail}.`, ...tolls, `Szacunek: ${caveats}.`].join(' ');
  return (
    <Tooltip title={title}>
      <Typography variant={variant} sx={sx}>
        {prefix}~{pln(cost.totalPln)}
      </Typography>
    </Tooltip>
  );
};
